import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMeetings } from "@/providers/MeetingsProvider";
import { useToast } from "@/hooks/use-toast";
import type { MeetingDto } from "@shared/types/MeetingDto";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

interface RescheduleMeetingDialogProps {
  meeting: MeetingDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RescheduleMeetingDialog({
  meeting,
  open,
  onOpenChange,
}: RescheduleMeetingDialogProps) {
  const { performAction, isActionLoading } = useMeetings();
  const { toast } = useToast();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("");
  const [timezone, setTimezone] = useState("");
  const [notifyParticipants, setNotifyParticipants] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (meeting && open) {
      if (meeting.scheduledAt) {
        const d = new Date(meeting.scheduledAt);
        setDate(d.toISOString().split("T")[0]);
        setTime(d.toTimeString().slice(0, 5));
      } else {
        setDate("");
        setTime("");
      }
      setDuration(String(meeting.durationMinutes || 30));
      setTimezone(
        meeting.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      setValidationError(null);
    }
  }, [meeting, open]);

  const validate = (): boolean => {
    if (!date || !time) {
      setValidationError("Please select a date and time.");
      return false;
    }

    const selected = new Date(`${date}T${time}`);
    if (selected <= new Date()) {
      setValidationError("Cannot schedule in the past.");
      return false;
    }

    const dur = parseInt(duration, 10);
    if (isNaN(dur) || dur <= 0) {
      setValidationError("Duration must be a positive number.");
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleConfirm = async () => {
    if (!meeting || !validate()) return;

    const newScheduledAt = new Date(`${date}T${time}`);

    try {
      await performAction(meeting.id, {
        action: "RESCHEDULE",
        rescheduleData: {
          newScheduledAt: newScheduledAt.toISOString(),
          newDurationMinutes: parseInt(duration, 10),
          newTimezone: timezone,
        },
      });

      toast({
        title: "Meeting rescheduled",
        description: notifyParticipants
          ? "Notifications sent to participants."
          : "Meeting rescheduled successfully.",
        variant: "success",
      });

      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Please try again.";
      if (message.includes("409") || message.includes("conflict")) {
        setValidationError(
          "Scheduling conflict detected. Please choose another time."
        );
      } else {
        toast({
          title: "Failed to reschedule",
          description: message,
          variant: "destructive",
        });
      }
    }
  };

  if (!meeting) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        aria-label="Reschedule meeting dialog"
      >
        <DialogHeader>
          <DialogTitle>Reschedule meeting</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="font-medium">{meeting.title}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">Date</Label>
              <Input
                id="reschedule-date"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setValidationError(null);
                }}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reschedule-time">Time</Label>
              <Input
                id="reschedule-time"
                type="time"
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  setValidationError(null);
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reschedule-duration">Duration (minutes)</Label>
            <Input
              id="reschedule-duration"
              type="number"
              min="1"
              value={duration}
              onChange={(e) => {
                setDuration(e.target.value);
                setValidationError(null);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="notify-reschedule"
              checked={notifyParticipants}
              onCheckedChange={(checked) =>
                setNotifyParticipants(checked === true)
              }
            />
            <Label
              htmlFor="notify-reschedule"
              className="font-normal cursor-pointer"
            >
              Notify participants
            </Label>
          </div>

          {validationError && (
            <p className="text-sm text-destructive">{validationError}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!date || !time || isActionLoading}
            aria-label={`Confirm reschedule meeting: ${meeting.title}`}
          >
            {isActionLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              "Confirm reschedule"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
